import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { RegisteredInfo, User } from '../commonTypes';
import { failedResponses } from '../errors/responses';
import ListResponse from './listResponse';
import { Region } from './region';

const c = initContract();

export const PartnerRegisteredInfo = RegisteredInfo;

export type PartnerRegisteredInfo = z.infer<typeof PartnerRegisteredInfo>;

export enum ContractStatus {
  Pending = 0,
  Applied = 1,
  NdaSigned = 2,
  ContractSigned = 3
}

export const PartnerPropertyBag = z.object({
  allowCustomLogo: z.boolean().optional()
});

export const Partner = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
  ingramId: z.string(),
  disabled: z.boolean(),
  registeredInfo: PartnerRegisteredInfo,
  catalogId: z.string(),
  contractStatus: z.nativeEnum(ContractStatus),
  propertyBag: PartnerPropertyBag,
  mspCreditCheckId: z.string().optional(),
  region: Region,
  enableCustomNotifications: z.boolean().optional()
});

export type Partner = z.infer<typeof Partner>;

const Partners = z.object({
  items: z.array(Partner)
});
type Partners = z.infer<typeof Partners>;

const PartnerCode = z.object({
  name: z.string(),
  code: z.string()
});

export type PartnerCode = z.infer<typeof PartnerCode>;

const PartnerCodeListResponse = ListResponse(PartnerCode);
type PartnerCodeListResponse = z.infer<typeof PartnerCodeListResponse>;

export type PartnerUser = z.infer<typeof User>;

export const HaloMappingDto = z.object({
  haloClientId: z.string().optional().nullable()
});

export type HaloMappingDto = z.infer<typeof HaloMappingDto>;

export default c.router({
  getPartners: {
    method: 'GET',
    path: '/partners',
    responses: {
      200: Partners,
      ...failedResponses
    }
  },
  getPartner: {
    method: 'GET',
    path: '/partners/:id',
    responses: {
      200: Partner,
      ...failedResponses
    }
  },
  getPartnerContract: {
    method: 'GET',
    path: '/msp/:id/contract',
    responses: {
      200: z.object({
        signedDocumentPath: z.string(),
        certificatePath: z.string()
      }),
      ...failedResponses
    }
  },
  getPartnerNda: {
    method: 'GET',
    path: '/msp/:id/nda',
    responses: {
      200: z.object({
        signedDocumentPath: z.string(),
        certificatePath: z.string()
      }),
      ...failedResponses
    }
  },
  createPartnerNda: {
    method: 'POST',
    path: '/msp/:id/nda',
    responses: {
      200: z.object({
        viewUrl: z.string()
      }),
      ...failedResponses
    },
    body: z.object({
      regNumber: z.string(),
      regAddress: z.string(),
      signerEmail: z.string(),
      signerName: z.string(),
      signerPosition: z.string(),
      returnUrl: z.string()
    }),
  },
  createPartnerContract: {
    method: 'POST',
    path: '/msp/:id/contract',
    responses: {
      200: z.object({
        viewUrl: z.string()
      }),
      ...failedResponses
    },
    body: z.object({
      returnUrl: z.string()
    }),
  },
  updatePartnerContract: {
    method: 'PUT',
    path: '/msp/:id/contract',
    responses: {
      200: z.object({
        status: z.string()
      }),
      ...failedResponses
    },
    body: z.object({
    }),
  },
  createPartner: {
    method: 'POST',
    path: '/partners',
    responses: {
      200: Partner,
      ...failedResponses
    },
    body: z.object({
      name: z.string(),
      code: z.string(),
      regionId: z.string(),
      registeredInfo: PartnerRegisteredInfo,
      propertyBag: PartnerPropertyBag
    }),
    summary: 'Create a partner'
  },
  deletePartner: {
    method: 'DELETE',

    path: '/partners/:id',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.undefined(),
    summary: 'Delete a partner'
  },
  updatePartner: {
    method: 'PUT',
    path: '/partners/:id',
    responses: {
      200: Partner,
      ...failedResponses
    },
    body: z.object({
      name: z.string(),
      code: z.string(),
      disabled: z.boolean(),
      regionId: z.string(),
      registeredInfo: PartnerRegisteredInfo,
      propertyBag: PartnerPropertyBag,
      enableCustomNotifications: z.boolean().optional()
    }),
    summary: 'Update a partner'
  },
  createPartnerCode: {
    method: 'POST',
    path: '/partners/code',
    responses: {
      200: PartnerCodeListResponse,
      ...failedResponses
    },
    body: z.object({
      names: z.array(z.string())
    }),
    summary: 'Create a partner code'
  },
  getPartnerUsers: {
    method: 'GET',
    path: '/partners/:id/users',
    responses: {
      200: ListResponse(User),
      ...failedResponses
    },
    summary: 'Get users for a partner'
  },
  deletePartnerUser: {
    method: 'DELETE',
    path: '/partners/:partnerId/users/:id',
    responses: {
      200: z.undefined(),
      ...failedResponses
    },
    body: z.undefined(),
    summary: 'Delete a user from a partner'
  },
  getPartnerHaloMapping: {
    method: 'GET',
    path: '/partners/:id/halo-mappings',
    responses: {
      200: HaloMappingDto,
      ...failedResponses
    }
  },
  updatePartnerHaloMapping: {
    method: 'PUT',
    path: '/partners/:id/halo-mappings',
    responses: {
      204: z.undefined(),
      ...failedResponses
    },
    body: HaloMappingDto
  }
});
